import PropTypes from 'prop-types'
import { PureComponent } from 'react'
import {
    map,
    flatten,
    intersection,
    intersectionBy,
    intersectionWith,
    uniq,
    isNumber,
    isEqual,
    findIndex,
} from 'lodash'
import VideoTitle from './video-title'
import VideoDescription from './video-description'
import Source from './source'
import Username from './username'
import Rating from './rating'
import ContentURL from './content-url'
import Tags from './tags'
import { getUrl } from '../../../util/gif-extract'
import { fitGifToSize } from '../../../util/sizing'
import { fetchGifDetailViewCount, fetchTagCounts } from 'shared/api/index'
import getViewCountDisplay from 'shared/util/view-count-formatter'
import gifProps from '../../../prop-types/gif'
import css from './info.css'
import styled from 'styled-components'
import EditPanelContext from '../context'
import { giphyYellow } from '../../../css/colors.css'
import PrivacyToggle from './privacy-toggle'

const getPropFromGifs = (gifs, prop) => {
    if (prop === 'user') {
        // Need to use a deeper comparator to intersect the user property.
        // We specify our own here, which compares the usernames of the users.
        return intersectionWith(map(gifs, 'user'), (a, b) => a && b && a.username === b.username).length === 1
            ? gifs[0].user
            : null
    } else {
        return intersectionBy(gifs, gif => gif[prop]).length === 1 ? gifs[0][prop] : null
    }
}

const getTagsFromGifs = gifs => {
    const isMultipleGifs = gifs.length > 1
    const gifTags = gifs.map(gif => gif.tags || [])
    const allTags = uniq(flatten(gifTags))
    const commonTags = intersection(...gifTags)
    const allFeaturedTags = gifs.map(gif => gif.featured_tags || [])
    const commonFeaturedTags = intersection(...allFeaturedTags)

    return allTags
        .map(tag => ({
            tag,
            isCommon: isMultipleGifs && commonTags.indexOf(tag) > -1,
            isFeatured: commonFeaturedTags.indexOf(tag) > -1,
        }))
        .sort((a, b) => a.tag.toLowerCase().localeCompare(b.tag.toLowerCase())) // sort tags alphabetically
}

// Returns true if any of the GIFs belong to a private channel, false otherwise.
const containsPrivateChannel = gifs => {
    return findIndex(gifs, g => g.user && !g.user.is_public) !== -1
}

const Metadata = styled.div`
    width: 100%;
    margin-right: ${props => (props.bulkEditMode ? `0px` : `25px`)};
`

const GifWrap = styled.div`
    flex-shrink: 0;
    position: relative;
    margin: 0 auto;
    height: ${props => props.height}px;
    width: ${props => props.width}px;
`

const PrivacyViewCountWrap = styled.div`
    height: 25px;
    margin-top: 15px;
    margin-bottom: 25px;
`

const GifCount = styled.div`
    font-family: 'NexaBlack';
    pointer-events: none;
    background-color: ${giphyYellow};
    color: black;
    font-weight: 900;
    font-size: 18px;
    height: 34px;
    width: 34px;
    position: absolute;
    top: 0;
    left: 0;
    line-height: 34px;
    text-align: center;
`

/**
 * Edit Panel - Info: Controls the info section
 * of the GIF Edit Panel. This allows tagging,
 * editing source, content url, etc.
 *
 * @class EditPanelInfo
 */
class EditPanelInfo extends PureComponent {
    state = {
        gifIndex: 0,
        viewCount: null,
        tagCounts: {},
    }
    static contextType = EditPanelContext
    static propTypes = {
        gifs: PropTypes.arrayOf(gifProps),
        permissions: PropTypes.object.isRequired,
        onChange: PropTypes.func.isRequired,
        user: PropTypes.object.isRequired,
        onTogglePrivacy: PropTypes.func.isRequired,
    }
    async updateViewCount() {
        const { gifs } = this.props

        this.setState({ viewCount: null })

        if (gifs.length === 1) {
            const gifId = gifs[0].id
            const data = await fetchGifDetailViewCount(gifId)
            this.setState({ viewCount: data.viewCount })
        }
    }
    async updateTagCounts() {
        const { gifs, permissions } = this.props

        this.setState({ tagCounts: {} })

        if (permissions.tag_counts && gifs.length === 1) {
            const gifId = gifs[0].id
            const data = await fetchTagCounts(gifId)
            this.setState({ tagCounts: data.tag_counts })
        }
    }
    componentDidMount() {
        this.updateViewCount()
        this.updateTagCounts()
    }
    componentDidUpdate(prevProps) {
        if (!isEqual(this.props.gifs, prevProps.gifs) || !isEqual(this.props.permissions, prevProps.permissions)) {
            this.setState({ gifIndex: 0 })
            this.updateViewCount()
            this.updateTagCounts()
        }
    }
    nextGif() {
        const { gifs } = this.props
        const { gifIndex: currentIndex } = this.state
        const gifIndex = currentIndex >= gifs.length - 1 ? 0 : currentIndex + 1
        this.setState({ gifIndex })
    }
    render() {
        const { gifs, permissions, onChange, user, onTogglePrivacy } = this.props
        const { gifIndex, viewCount, tagCounts } = this.state
        const { bulkEditMode } = this.context
        const gifUser = getPropFromGifs(gifs, 'user')
        const isPrivate = containsPrivateChannel(gifs)
        const source = getPropFromGifs(gifs, 'source_post_url')
        const username = getPropFromGifs(gifs, 'username')
        const rating = getPropFromGifs(gifs, 'rating')
        const contentUrl = getPropFromGifs(gifs, 'source_content_url')
        // we want the preview to show the "most recently selected" gif
        const gif = gifs[gifs.length - 1 - gifIndex]
        const isSticker = gif.is_sticker
        const tags = getTagsFromGifs(gifs)
        const previewWidth = bulkEditMode ? 280 : 230
        const previewHeight = bulkEditMode ? 195 : 160

        const isVideo = gifs.length === 1 && gif.type === 'video'

        const gifPreview = (
            <div>
                <GifWrap bulkEditMode={bulkEditMode} width={previewWidth} height={previewHeight}>
                    <div
                        onClick={() => this.nextGif()}
                        className={isSticker ? css.sticker : css.gif}
                        data-isstack={gifs.length > 1}
                        style={fitGifToSize(gif, previewWidth, previewHeight)}
                    >
                        <img className={css.previewImage} src={getUrl(gif, 'fixed_height')} key={gif.id} />
                        {bulkEditMode && gifs.length > 1 && <GifCount>{gifs.length}</GifCount>}
                    </div>
                </GifWrap>
                <PrivacyViewCountWrap>
                    {bulkEditMode && (
                        <PrivacyToggle
                            user={user}
                            permissions={permissions}
                            gifs={gifs}
                            onTogglePrivacy={onTogglePrivacy}
                        />
                    )}
                    {isNumber(viewCount) && <div className={css.viewCount}>{getViewCountDisplay(viewCount)}</div>}
                </PrivacyViewCountWrap>
            </div>
        )

        return (
            <div className={css.container}>
                {bulkEditMode ? gifPreview : null}
                <div className={css.metadataBlock}>
                    <Metadata bulkEditMode={bulkEditMode}>
                        {user.isStaff && (
                            <>
                                <Username
                                    className={css.section}
                                    avatar={gifUser ? gifUser.avatar : null}
                                    value={username}
                                    isPrivate={isPrivate}
                                    onChange={username => onChange({ username })}
                                />
                                {
                                    gifUser &&
                                    <a
                                        href={`https://admin.giphy.com/admin/users/user/${gifUser.id}/change/`}
                                        className={css.button}
                                        target="_blank"
                                    >
                                        View User Account
                                    </a>
                                }
                            </>
                        )}
                        {isVideo && (
                            <>
                                <VideoTitle
                                    className={css.section}
                                    value={gif.title}
                                    onChange={title => onChange({ title })}
                                />
                                <VideoDescription
                                    className={css.section}
                                    value={gif.video.description}
                                    onChange={description => onChange({ description })}
                                />
                            </>
                        )}
                        <Source
                            className={css.section}
                            value={source}
                            onChange={source_post_url => onChange({ source_post_url })}
                        />
                        {(permissions.rate || user.isStaff) && (
                            <Rating
                                className={css.section}
                                value={rating}
                                allowUnrated={user.isStaff}
                                onChange={rating => onChange({ rating })}
                            />
                        )}
                        {!isVideo && (
                            <ContentURL
                                value={contentUrl}
                                onChange={source_content_url => onChange({ source_content_url })}
                            />
                        )}
                    </Metadata>
                    {!bulkEditMode ? gifPreview : null}
                </div>
                {(permissions.tag || user.isStaff) && (
                    <Tags
                        className={css.contentBlock}
                        user={user}
                        permissions={permissions}
                        gifs={gifs}
                        tags={tags}
                        tagCounts={tagCounts}
                        isMultipleGifs={gifs.length > 1}
                        onAddTags={async tags => {
                            for (let tag of tags) {
                                await onChange({ create_tag: tag })
                            }
                        }}
                        onRemoveTag={tag => onChange({ delete_tag: tag })}
                        onRemoveAllTags={() => onChange({ delete_all_tags: true }, true)}
                        onFeatureTag={tag => onChange({ feature_tag: tag })}
                        onUnfeatureTag={tag => onChange({ unfeature_tag: tag })}
                    />
                )}
            </div>
        )
    }
}

export default EditPanelInfo
